import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { Building2, Users, Layers, KeySquare, AlertTriangle } from 'lucide-react';

const AdminDashboard = () => {
  const { data: stats, isLoading } = useQuery({
    queryKey: ['admin-stats'],
    queryFn: async () => {
      const [
        companiesResult,
        usersResult,
        systemsResult,
        plansResult
      ] = await Promise.all([
        supabase.from('companies').select('id', { count: 'exact' }),
        supabase.from('profiles').select('id', { count: 'exact' }),
        supabase.from('systems').select('id', { count: 'exact' }),
        supabase.from('company_system_plans').select('id', { count: 'exact' }).eq('status', 'active')
      ]);

      return {
        companies: companiesResult.count || 0,
        users: usersResult.count || 0,
        systems: systemsResult.count || 0,
        plans: plansResult.count || 0,
      };
    },
  });

  const { data: recentActivity } = useQuery({
    queryKey: ['recent-activity'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('audit_logs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(10);
      
      if (error) throw error;
      return data || [];
    },
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <div className="animate-pulse">
                  <div className="h-4 bg-muted rounded w-24 mb-2"></div>
                  <div className="h-8 bg-muted rounded w-16"></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  const kpis = [
    {
      title: 'Empresas',
      value: stats?.companies || 0,
      icon: Building2,
      description: 'Total de empresas cadastradas',
    },
    {
      title: 'Usuários',
      value: stats?.users || 0,
      icon: Users,
      description: 'Usuários ativos na plataforma',
    },
    {
      title: 'Sistemas',
      value: stats?.systems || 0,
      icon: Layers,
      description: 'Sistemas disponíveis',
    },
    {
      title: 'Planos Ativos',
      value: stats?.plans || 0,
      icon: KeySquare,
      description: 'Planos contratados ativos',
    },
  ];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h2 className="text-3xl font-bold tracking-tight">Dashboard Administrativo</h2>
        <p className="text-muted-foreground">
          Visão geral da plataforma PortalONE Suite
        </p>
      </div>

      {/* KPI Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {kpis.map((kpi, index) => (
          <Card key={kpi.title} className="border-l-4 border-l-primary">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                {kpi.title}
              </CardTitle>
              <kpi.icon className={`h-4 w-4 ${
                index === 0 ? 'text-blue-500' : 
                index === 1 ? 'text-purple-500' : 
                index === 2 ? 'text-cyan-500' : 'text-indigo-500'
              }`} />
            </CardHeader>
            <CardContent>
              <div className={`text-2xl font-bold ${
                index === 0 ? 'text-blue-600' : 
                index === 1 ? 'text-purple-600' : 
                index === 2 ? 'text-cyan-600' : 'text-indigo-600'
              }`}>{kpi.value}</div>
              <p className="text-xs text-muted-foreground">
                {kpi.description}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Recent Activity */}
      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Atividade Recente</CardTitle>
          </CardHeader>
          <CardContent>
            {recentActivity && recentActivity.length > 0 ? (
              <div className="space-y-4">
                {recentActivity.slice(0, 5).map((activity) => (
                  <div key={activity.id} className="flex items-center space-x-4 text-sm">
                    <div className="w-2 h-2 bg-primary rounded-full"></div>
                    <div className="flex-1">
                      <div className="font-medium">{activity.action}</div>
                      <div className="text-muted-foreground text-xs">
                        {activity.entity_type} • {new Date(activity.created_at).toLocaleString('pt-BR')}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground text-sm">
                Nenhuma atividade recente
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <AlertTriangle className="h-4 w-4" />
              <span>Alertas do Sistema</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 text-sm">
              <div className="p-3 bg-warning/10 border border-warning/20 rounded-lg">
                <div className="font-medium text-warning-foreground">
                  Sistema funcionando normalmente
                </div>
                <div className="text-muted-foreground text-xs">
                  Todos os serviços estão operacionais
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Ações Rápidas</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="text-center p-4 border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors">
              <Building2 className="h-8 w-8 mx-auto mb-2 text-primary" />
              <div className="font-medium">Nova Empresa</div>
              <div className="text-sm text-muted-foreground">Cadastrar empresa</div>
            </div>
            <div className="text-center p-4 border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors">
              <Users className="h-8 w-8 mx-auto mb-2 text-primary" />
              <div className="font-medium">Convidar Usuário</div>
              <div className="text-sm text-muted-foreground">Adicionar novo usuário</div>
            </div>
            <div className="text-center p-4 border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors">
              <KeySquare className="h-8 w-8 mx-auto mb-2 text-primary" />
              <div className="font-medium">Gerenciar Planos</div>
              <div className="text-sm text-muted-foreground">Configurar planos de sistema</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminDashboard;
